/**
 * Helper functions for handling criteria in both old and new formats
 *
 * Old format: values are simple strings
 * New format: values are objects with { identifier, value, display }
 */

/**
 * Normalize criteria group values to handle both formats
 * @param {Array} values - Array of values (could be strings or objects)
 * @returns {Array} Array of objects with { identifier, value }
 */
export const normalizeCriteriaValues = (values) => {
  if (!Array.isArray(values)) return [];

  return values.map(item => {
    // New format: already an object with identifier
    if (typeof item === 'object' && item !== null && item.identifier) {
      return {
        identifier: item.identifier,
        value: item.value || item.display || item.identifier
      };
    }

    // Old format: simple string - use it as both identifier and value
    // This provides backward compatibility
    if (typeof item === 'string') {
      return {
        identifier: item,
        value: item
      };
    }

    // Fallback
    return {
      identifier: String(item),
      value: String(item)
    };
  });
};

/**
 * Get the identifier from a value (handles both formats)
 * @param {string|object} value - Value in old or new format
 * @returns {string} The identifier
 */
export const getIdentifier = (value) => {
  if (typeof value === 'object' && value !== null) {
    return value.identifier || value.value || value.display || '';
  }
  return String(value || '');
};

/**
 * Get the display value from a value (handles both formats)
 * @param {string|object} value - Value in old or new format
 * @returns {string} The display value
 */
export const getDisplayValue = (value) => {
  if (typeof value === 'object' && value !== null) {
    return value.value || value.display || value.identifier || '';
  }
  return String(value || '');
};

/**
 * Check if a criteria value is selected
 * @param {Array} selectedValues - Array of selected identifiers
 * @param {string|object} optionValue - The option value to check
 * @returns {boolean} True if selected
 */
export const isCriteriaSelected = (selectedValues, optionValue) => {
  if (!Array.isArray(selectedValues)) return false;

  const optionIdentifier = getIdentifier(optionValue);

  // Check if any selected value matches this option
  return selectedValues.some(selected => {
    const selectedIdentifier = getIdentifier(selected);
    return selectedIdentifier === optionIdentifier;
  });
};

/**
 * Prepare criteria for saving to backend (always use identifiers)
 * @param {Object} criteriaObject - Criteria object with group names as keys
 * @param {Array} criteriaGroups - Available criteria groups from API
 * @returns {Object} Normalized criteria with identifiers as keys
 */
export const prepareCriteriaForSave = (criteriaObject, criteriaGroups) => {
  // Validate that criteriaGroups is loaded
  if (!criteriaGroups || criteriaGroups.length === 0) {
    console.error('❌ CRITICAL: criteriaGroups not loaded! Cannot convert criteria to identifiers');
    console.error('This will prevent translated values from being saved to the database');
    console.error('Falling back to saving as-is (may contain translated values)');
    // Return the criteria as-is for now to allow saving (temporary fallback)
    return criteriaObject;
  }

  const normalized = {};

  // Convert group names to identifiers
  Object.entries(criteriaObject).forEach(([groupKey, values]) => {
    if (!values || (Array.isArray(values) && values.length === 0)) {
      return; // Skip empty criteria
    }

    // Find the group to get its identifier
    const group = criteriaGroups.find(g =>
      g.identifier === groupKey || g.groupName === groupKey
    );

    if (!group) {
      console.error(`❌ Group not found for key: "${groupKey}"`);
      console.error(`Available groups:`, criteriaGroups.map(g => ({ identifier: g.identifier, groupName: g.groupName })));
      return; // Skip this group to prevent saving with translated key
    }

    const groupIdentifier = group.identifier || groupKey;
    console.log(`✅ Converting group "${groupKey}" → identifier: "${groupIdentifier}"`);

    // Helper function to convert a display value to its identifier
    const findIdentifierForValue = (displayValue) => {
      console.log(`    🔍 Finding identifier for display value: "${displayValue}" (type: ${typeof displayValue})`);

      // If it's already an object with identifier, extract it
      if (typeof displayValue === 'object' && displayValue !== null) {
        const identifier = displayValue.identifier || displayValue.value || displayValue.display || '';
        console.log(`      ✅ Value is object, extracted identifier: "${identifier}"`);
        return identifier;
      }

      // If it's a string, look it up in the group's values to find the identifier
      const displayStr = String(displayValue);
      console.log(`      🔎 Looking up "${displayStr}" in group "${group.groupName}" values:`);
      console.log(`      📋 Group has ${group.values?.length || 0} values`);

      // Try exact match first
      let option = group.values?.find(v => {
        const optionValue = typeof v === 'object' ? v.value : v;
        return optionValue === displayStr;
      });

      // If exact match fails, try case-insensitive match
      if (!option) {
        const lowerDisplayStr = displayStr.toLowerCase();
        option = group.values?.find(v => {
          const optionValue = typeof v === 'object' ? v.value : v;
          return optionValue.toLowerCase() === lowerDisplayStr;
        });
        if (option) {
          console.log(`      ✅ Found case-insensitive match for "${displayStr}"`);
        }
      }

      // If still not found, try matching by identifier (maybe it's already an identifier)
      if (!option) {
        option = group.values?.find(v => {
          const optionIdentifier = typeof v === 'object' ? v.identifier : v;
          return optionIdentifier === displayStr || optionIdentifier.toLowerCase() === displayStr.toLowerCase();
        });
        if (option) {
          console.log(`      ✅ Value is already an identifier: "${displayStr}"`);
        }
      }

      if (option) {
        const identifier = typeof option === 'object' ? option.identifier : option;
        console.log(`      ✅ Final identifier: "${identifier}"`);
        return identifier;
      }

      // No match found - warn but allow saving
      console.warn(`      ⚠️ Could not find identifier for value "${displayValue}" in group "${group.groupName}"`);
      console.warn(`      Available values:`, group.values?.map(v => typeof v === 'object' ? `${v.value} (${v.identifier})` : v));
      console.warn(`      Falling back to using the value as-is: "${displayStr}"`);
      return displayStr; // Fallback: return as-is to allow saving
    };

    // Convert values to identifiers
    if (Array.isArray(values)) {
      const convertedValues = values.map(v => findIdentifierForValue(v));
      normalized[groupIdentifier] = convertedValues;
    } else {
      normalized[groupIdentifier] = findIdentifierForValue(values);
    }
  });

  console.log('📤 Final normalized criteria:', normalized);
  return normalized;
};

/**
 * Load criteria from backend (handle both old and new formats)
 * @param {Object} savedCriteria - Criteria loaded from backend
 * @param {Array} criteriaGroups - Available criteria groups from API
 * @returns {Object} Criteria ready for formData
 */
export const loadCriteriaFromBackend = (savedCriteria, criteriaGroups) => {
  if (!savedCriteria || typeof savedCriteria !== 'object') {
    return {};
  }

  const loaded = {};

  Object.entries(savedCriteria).forEach(([key, values]) => {
    // Find matching group (by identifier or groupName for backward compat)
    const group = criteriaGroups.find(g =>
      g.identifier === key || g.groupName === key
    );

    if (!group) {
      console.warn(`Criteria group not found for key: ${key}`);
      return;
    }

    // Use the current groupName as the key (for UI display)
    // But the values should be identifiers for matching
    const groupKey = group.groupName;

    if (Array.isArray(values)) {
      loaded[groupKey] = values;
    } else if (values) {
      loaded[groupKey] = [values];
    }
  });

  return loaded;
};
